import { Action, Close, Description, Provider, Root, Title, Viewport } from '@radix-ui/react-toast';
import { ComponentPropsWithoutRef, ElementRef, ReactElement, forwardRef } from 'react';
import classNames from '@/lib/classNames';
import { type VariantProps, cva } from 'class-variance-authority';
import { Icon } from '../../global/Icons';
import { useToast } from './useToast';

const ToastProvider = Provider;

const ToastViewport = forwardRef<ElementRef<typeof Viewport>, ComponentPropsWithoutRef<typeof Viewport>>(({ className, ...props }, ref) => (
    <Viewport
        ref={ref}
        className={classNames('fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]', className)}
        {...props}
    />
));
ToastViewport.displayName = Viewport.displayName;

const toastVariants = cva(
    'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
    {
        variants: {
            variant: {
                default: 'border border-white bg-primary text-white',
                destructive: 'destructive group border-destructive bg-destructive text-white',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

const Toast = forwardRef<ElementRef<typeof Root>, ComponentPropsWithoutRef<typeof Root> & VariantProps<typeof toastVariants>>(({ className, variant, ...props }, ref) => {
    return <Root ref={ref} className={classNames(toastVariants({ variant }), className)} {...props} />;
});
Toast.displayName = Root.displayName;

const ToastAction = forwardRef<ElementRef<typeof Action>, ComponentPropsWithoutRef<typeof Action>>(({ className, ...props }, ref) => (
    <Action
        ref={ref}
        className={classNames(
            'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive group-[.destructive]:focus:ring-destructive',
            className
        )}
        {...props}
    />
));
ToastAction.displayName = Action.displayName;

const ToastClose = forwardRef<ElementRef<typeof Close>, ComponentPropsWithoutRef<typeof Close>>(({ className, ...props }, ref) => (
    <Close
        ref={ref}
        className={classNames(
            'absolute right-2 top-2 rounded-md p-1 text-white/50 transition-opacity hover:text-white focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
            className
        )}
        toast-close=""
        {...props}
    >
        <Icon id={'cross'} className="w-4 h-4" fill="currentColor" />
    </Close>
));
ToastClose.displayName = Close.displayName;

const ToastTitle = forwardRef<ElementRef<typeof Title>, ComponentPropsWithoutRef<typeof Title>>(({ className, ...props }, ref) => (
    <Title ref={ref} className={classNames('p small font-semibold', className)} {...props} />
));
ToastTitle.displayName = Title.displayName;

const ToastDescription = forwardRef<ElementRef<typeof Description>, ComponentPropsWithoutRef<typeof Description>>(({ className, ...props }, ref) => (
    <Description ref={ref} className={classNames('p small opacity-90', className)} {...props} />
));
ToastDescription.displayName = Description.displayName;

function Toaster() {
    const { toasts } = useToast();

    return (
        <ToastProvider>
            {toasts.map(function ({ id, title, description, action, ...props }) {
                return (
                    <Toast key={id} {...props}>
                        <div className="grid gap-1">
                            {title && <ToastTitle>{title}</ToastTitle>}
                            {description && <ToastDescription>{description}</ToastDescription>}
                        </div>
                        {action}
                        <ToastClose />
                    </Toast>
                );
            })}
            <ToastViewport />
        </ToastProvider>
    );
}

type ToastProps = ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = ReactElement<typeof ToastAction>;

export { type ToastProps, type ToastActionElement, ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction, Toaster };
